import { EcoCard, EcoText, EcoToggle } from '@components/shared';
import { MerchantAdmin } from '@ecocart/entities';
import { addEcoCartScript, getShopifyScripts, removeEcoCartScript } from '@utils/api/shopify';
import { Gap } from '@utils/layout';
import { colors, spacing } from '@utils/tailwind';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { twMerge } from 'tailwind-merge';
import { MerchantActivityCardDetails, useMerchantActivityCardConfig } from './MerchantActivityCard.utils';

interface Props {
  merchantAdmin: MerchantAdmin;
}

export default function MerchantActivityCard({ merchantAdmin }: Props): JSX.Element {
  const { shopName, active, createdAt, updatedAt, variantIds, ecocartEnabled, ecocartActive, platform } = merchantAdmin || {};
  const [hasScript, setHasScript] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const formConfig = useMerchantActivityCardConfig({
    createdAt,
    updatedAt,
    variantIds,
    ecocartActive,
    ecocartEnabled,
    platform,
    hasScript
  });

  useEffect(() => {
    if (platform === 'Shopify') {
      getShopifyScripts(shopName).then((scripts) => {
        if (scripts?.script_tags?.[0]?.src) {
          setHasScript(scripts.script_tags[0].src.includes('ecocart.js'));
        } else {
          setHasScript(false);
        }
      });
    }
  }, [shopName, platform]);

  const topComponent = () => {
    if (platform !== 'api') {
      return active ? (
        <EcoText color="success" fontWeight="semibold">
          Installed
        </EcoText>
      ) : (
        <EcoText color="danger" fontWeight="semibold">
          Uninstalled
        </EcoText>
      );
    }
    return undefined;
  };

  const handleScriptToggle = async () => {
    setLoading(true);
    if (hasScript) {
      await removeEcoCartScript(shopName).then(() => {
        setHasScript(false);
      });
    } else {
      await addEcoCartScript(shopName).then(() => {
        setHasScript(true);
      });
    }
    setLoading(false);
  };

  return merchantAdmin ? (
    <>
      <EcoCard
        title={shopName}
        topRightComponent={topComponent()}
        className={twMerge('border', active ? 'border-success' : 'border-danger-700')}
        style={{ padding: Number(spacing['4'].replace('px', '')), backgroundColor: active ? colors.success[50] : colors.danger[50] }}
      >
        <ScrollView horizontal className="mt-4" contentContainerStyle={{ width: '100%' }}>
          <View className="flex-col lg:flex-row space-evenly w-full" style={Gap()}>
            {formConfig.map(({ label, value }) => (
              <MerchantActivityCardDetails key={label} label={label} value={value} />
            ))}
          </View>
        </ScrollView>
      </EcoCard>
      <View className="z-[10001] flex flex-row justify-between">
        <EcoText>Ecocart Script on Store</EcoText>
        <EcoToggle disabled={loading || !active} value={hasScript} onValueChange={handleScriptToggle} />
      </View>
    </>
  ) : (
    <></>
  );
}
